import { useState, useEffect } from "react";
import { locations as locsApi } from "../lib/supabase.js";

export default function LocationEditor({ location, map, color, typeOptions = [], onSave, onClose }) {
  const customFields = map?.custom_fields || [];
  const isNew = !location?.id;
  // Location Type is the tenant's own vocabulary ("RV Dealer", "Jeep Dealer",
  // whatever a map uses): offer the types already on this map as a dropdown,
  // with "+ New type…" to coin one. A type on the record that isn't in the
  // list (e.g. legacy value) still shows as a choice so edits don't clobber it.
  const knownTypes = [...new Set([...(typeOptions || []), ...(location?.location_type ? [location.location_type] : [])])];
  const [customType, setCustomType] = useState(false);

  const [form, setForm] = useState({
    name: "", description: "", location_type: "",
    address_line1: "", address_line2: "", city: "", state_province: "", postal_code: "", country: "US",
    formatted_address: "",
    latitude: "", longitude: "",
    phone: "", email: "", website_url: "",
    services: [],
    is_active: true,
    attributes: {},
  });
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (location) {
      setForm({
        name: location.name || "", description: location.description || "", location_type: location.location_type || "",
        address_line1: location.address_line1 || "", address_line2: location.address_line2 || "", city: location.city || "", state_province: location.state_province || "", postal_code: location.postal_code || location.zip_code || "", country: location.country || "US",
        formatted_address: location.formatted_address || "",
        latitude: location.latitude ?? "", longitude: location.longitude ?? "",
        phone: location.phone || "", email: location.email || "", website_url: location.website_url || "",
        services: location.services || [],
        is_active: location.is_active !== false,
        attributes: location.attributes || {},
      });
    }
  }, [location]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setAttr = (k, v) => setForm((p) => ({ ...p, attributes: { ...p.attributes, [k]: v } }));

  // ── Geocode: fill lat/lng from the address fields ─────────────────────────
  // Tries Google's Geocoding REST with the map's key first (works when the key
  // isn't referrer-restricted), then falls back to OpenStreetMap Nominatim
  // (no key, fine for occasional dashboard use). Never blocks manual entry.
  const geocodeFromAddress = async () => {
    const parts = [form.address_line1, form.city, form.state_province, form.postal_code, form.country].filter(Boolean);
    if (parts.length < 2) { setError("Enter at least a street address and city (or zip) first, then try again."); return; }
    setGeocoding(true); setError(null);
    const q = parts.join(", ");
    try {
      let lat = null, lng = null, formatted = null;
      if (map?.google_maps_api_key) {
        try {
          const r = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(q)}&key=${map.google_maps_api_key}`);
          const j = await r.json();
          if (j.status === "OK" && j.results?.[0]) {
            lat = j.results[0].geometry.location.lat;
            lng = j.results[0].geometry.location.lng;
            formatted = j.results[0].formatted_address;
          }
        } catch { /* fall through to Nominatim */ }
      }
      if (lat == null) {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`, { headers: { Accept: "application/json" } });
        const j = await r.json();
        if (j?.[0]) { lat = parseFloat(j[0].lat); lng = parseFloat(j[0].lon); }
      }
      if (lat == null || Number.isNaN(lat)) {
        setError(`Couldn't find coordinates for "${q}". Check the address, or enter latitude/longitude manually (right-click the spot in Google Maps and click the coordinates to copy them).`);
      } else {
        setForm((p) => ({
          ...p,
          latitude: String(lat.toFixed ? lat.toFixed(6) : lat),
          longitude: String(lng.toFixed ? lng.toFixed(6) : lng),
          formatted_address: p.formatted_address || formatted || "",
        }));
      }
    } catch (e) {
      setError(`Coordinate lookup failed: ${e.message}. You can enter latitude/longitude manually.`);
    }
    setGeocoding(false);
  };

  const handleSave = async () => {
    const missing = [];
    if (!form.name.trim()) missing.push("Name");
    if (!form.latitude) missing.push("Latitude");
    if (!form.longitude) missing.push("Longitude");
    if (missing.length) {
      setError(`Required before saving: ${missing.join(", ")}.` +
        (missing.includes("Latitude") || missing.includes("Longitude")
          ? ' Use "Get coordinates from address" below the address fields, or enter them manually.'
          : ""));
      return;
    }
    setSaving(true); setError(null);

    const payload = {
      ...form,
      map_id: map.id,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      formatted_address: form.formatted_address || [form.address_line1, form.city, form.state_province, form.postal_code].filter(Boolean).join(", "),
    };

    let result;
    if (location?.id) {
      result = await locsApi.update(location.id, payload);
    } else {
      result = await locsApi.create(payload);
    }

    if (result.error) setError(result.error?.message || String(result.error));
    else { onSave?.(result.data); onClose(); }
    setSaving(false);
  };

  const inputStyle = { width: "100%", padding: "8px 12px", background: "#0a0a10", border: "1px solid #1e1e2e", borderRadius: 6, color: "#ccc", fontSize: 13, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, fontWeight: 700 };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#111118", border: "1px solid #1e1e2e", borderRadius: 14, padding: 28, width: 600, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{isNew ? "Add Location" : "Edit Location"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#555", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        {error && <div style={{ padding: "8px 12px", borderRadius: 6, marginBottom: 14, fontSize: 12, background: "#2a0a0a", color: "#f87171", border: "1px solid #991b1b" }}>{error}</div>}

        {/* Core fields */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Name *</label><input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Business Name" style={inputStyle} /></div>
          <div>
            <label style={labelStyle}>Location Type</label>
            {knownTypes.length > 0 && !customType ? (
              <select
                value={knownTypes.includes(form.location_type) ? form.location_type : ""}
                onChange={(e) => { if (e.target.value === "__new__") { setCustomType(true); set("location_type", ""); } else set("location_type", e.target.value); }}
                style={{ ...inputStyle, cursor: "pointer" }}>
                <option value="">— Choose a type —</option>
                {knownTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="__new__">+ New type…</option>
              </select>
            ) : (
              <div style={{ display: "flex", gap: 6 }}>
                <input value={form.location_type} onChange={(e) => set("location_type", e.target.value)} placeholder={knownTypes[0] ? `e.g. ${knownTypes[0]}` : "e.g. Authorized Dealer"} style={inputStyle} autoFocus={customType} />
                {knownTypes.length > 0 && <button type="button" onClick={() => setCustomType(false)} title="Back to the type list" style={{ background: "none", border: "1px solid #1e1e2e", borderRadius: 6, color: "#888", fontSize: 11, cursor: "pointer", padding: "0 10px", flexShrink: 0 }}>List</button>}
              </div>
            )}
          </div>
          <div><label style={labelStyle}>Phone</label><input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" style={inputStyle} /></div>
          <div><label style={labelStyle}>Email</label><input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="info@business.com" style={inputStyle} /></div>
          <div><label style={labelStyle}>Website</label><input value={form.website_url} onChange={(e) => set("website_url", e.target.value)} placeholder="https://..." style={inputStyle} /></div>
        </div>

        {/* Address */}
        <div style={{ marginBottom: 16, paddingTop: 12, borderTop: "1px solid #1a1a2a" }}>
          <div style={{ fontSize: 11, color: "#666", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Address</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
            <div><label style={labelStyle}>Address Line 1</label><input value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} style={inputStyle} /></div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 8 }}>
              <div><label style={labelStyle}>City</label><input value={form.city} onChange={(e) => set("city", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>State</label><input value={form.state_province} onChange={(e) => set("state_province", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Zip</label><input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Country</label><input value={form.country} onChange={(e) => set("country", e.target.value)} style={inputStyle} /></div>
            </div>
          </div>
        </div>

        {/* Coordinates */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: "#666", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Coordinates *</div>
          <button onClick={geocodeFromAddress} disabled={geocoding} style={{ padding: "6px 12px", background: `${color}15`, border: `1px solid ${color}40`, borderRadius: 6, color, fontSize: 11, fontWeight: 600, cursor: geocoding ? "wait" : "pointer", fontFamily: "inherit", opacity: geocoding ? 0.6 : 1 }}>{geocoding ? "Looking up\u2026" : "\ud83d\udccd Get coordinates from address"}</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div><label style={labelStyle}>Latitude *</label><input type="number" step="0.000001" value={form.latitude} onChange={(e) => set("latitude", e.target.value)} placeholder="41.6837" style={inputStyle} /></div>
          <div><label style={labelStyle}>Longitude *</label><input type="number" step="0.000001" value={form.longitude} onChange={(e) => set("longitude", e.target.value)} placeholder="-85.9767" style={inputStyle} /></div>
        </div>

        {/* Custom fields from map config */}
        {customFields.length > 0 && (
          <div style={{ paddingTop: 12, borderTop: "1px solid #1a1a2a", marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: "#666", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Custom Fields</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {customFields.map((cf) => (
                <div key={cf.key}>
                  <label style={labelStyle}>{cf.label}</label>
                  {cf.type === "boolean" ? (
                    <button onClick={() => setAttr(cf.key, !form.attributes[cf.key])} style={{ padding: "8px 14px", background: form.attributes[cf.key] ? `${color}15` : "#0a0a10", border: `1px solid ${form.attributes[cf.key] ? `${color}40` : "#1e1e2e"}`, borderRadius: 6, color: form.attributes[cf.key] ? color : "#555", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{form.attributes[cf.key] ? "✓ Yes" : "No"}</button>
                  ) : cf.type === "select" ? (
                    <select value={form.attributes[cf.key] || ""} onChange={(e) => setAttr(cf.key, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">Select...</option>
                      {(cf.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : cf.type === "tags" ? (
                    <input value={(form.attributes[cf.key] || []).join(", ")} onChange={(e) => setAttr(cf.key, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="tag1, tag2" style={inputStyle} />
                  ) : (
                    <input value={form.attributes[cf.key] || ""} onChange={(e) => setAttr(cf.key, e.target.value)} placeholder={cf.label} type={cf.type === "number" ? "number" : cf.type === "url" ? "url" : "text"} style={inputStyle} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "#0a0a10", borderRadius: 8, border: "1px solid #1a1a2a", marginBottom: 20 }}>
          <span style={{ fontSize: 13, color: "#ccc" }}>Active</span>
          <button onClick={() => set("is_active", !form.is_active)} style={{ width: 40, height: 22, borderRadius: 11, border: "none", cursor: "pointer", background: form.is_active ? "#22c55e" : "#333", position: "relative" }}>
            <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: form.is_active ? 21 : 3, transition: "left 0.2s" }} />
          </button>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ padding: "10px 20px", background: "#1a1a2a", border: "1px solid #2a2a3a", borderRadius: 8, color: "#888", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          {error && <div style={{ padding: "8px 12px", borderRadius: 6, marginBottom: 10, fontSize: 12, background: "#2a0a0a", color: "#f87171", border: "1px solid #991b1b" }}>{error}</div>}
          <button onClick={handleSave} disabled={saving} style={{ padding: "10px 24px", background: color, border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: saving ? 0.5 : 1 }}>{saving ? "Saving..." : isNew ? "Add Location" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}
